import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Loader2, Check, AlertTriangle } from "lucide-react";
import { cn } from "../../../../utils/cn";
import { Card } from "../../../../components/ui/Card";
import { Skeleton } from "../../../../components/ui/Skeleton";
import { PendingBackendNotice } from "../../../../components/ui/PendingBackendNotice";
import {
  getAiSettings,
  getAiChannelScope,
  getAiModelCatalog,
  saveAiSettings,
  testAiSettings,
  isEndpointMissing,
} from "./aiSettingsApi";
import {
  AI_MODEL_CATALOG,
  AI_BADGE_LABELS,
  AI_MODEL_MAX,
  normalizeAiCatalog,
  recommendedModelId,
} from "./aiModelCatalog";
import {
  initialScopeStates,
  buildAccountOverrides,
  disabledScopeLabels,
  followChannel,
  formatScopePhone,
} from "./aiScope";
import KnowledgeBaseEditor from "./KnowledgeBaseEditor";
import { aiErrorMessage, isGrantable, normalizeCaller } from "./aiErrorMessage";

// Asistente IA (BYO): el tenant conecta su propia cuenta de IA (Anthropic,
// OpenAI, Gemini o Kimi — estos tres últimos vía endpoints OpenAI-compatibles
// del backend) para responder automáticamente los mensajes entrantes del
// helpdesk mientras la conversación no esté asignada a un agente humano.
//
// SEGURIDAD: el backend nunca devuelve el token completo — la vista solo
// conoce has_token/token_last4, el input es type=password y el token viaja
// únicamente en el body del PUT/POST cuando el usuario tipea uno nuevo.
// Prohibido loguear o renderizar el token.
//
// Este formulario es la configuración BASE del courier (el "agente principal"):
// el formulario original de AiSettings.jsx movido tal cual, con su propio estado,
// su propio GET/PUT a ai-settings y las mismas claves i18n. Vive aparte para que
// la página de agentes pueda mostrar en el mismo panel la lista, el editor de un
// agente o este formulario. El encabezado de página (PageHeader) y el layout del
// helpdesk los pone quien lo monta.

const PROMPT_MAX = 6000;

// Proveedores/modelos: la vista arranca con la copia local de
// aiModelCatalog.js y la reemplaza con GET catalog cuando el backend nuevo
// existe — así el selector funciona aunque esa ruta no esté desplegada.
// keyUrl (dónde se crea el token) también viene del catálogo.
const providerFromCatalog = (catalog, id) =>
  catalog.find((p) => p.id === id) || null;

// Capacidad del catálogo de agentes que equivale al interruptor "consultar
// pedidos" de esta configuración (mismo id que usa el backend para decidir si
// el rol puede conceder ese acceso).
const ORDERS_CAPABILITY_ID = "estado_pedido";

// Días de la semana, domingo=0 (convención del backend). Se PINTAN lunes
// primero (orden chileno) pero el value es el índice real.
const DAY_PILLS = [
  { value: 1, label: "L", name: "lunes" },
  { value: 2, label: "M", name: "martes" },
  { value: 3, label: "X", name: "miércoles" },
  { value: 4, label: "J", name: "jueves" },
  { value: 5, label: "V", name: "viernes" },
  { value: 6, label: "S", name: "sábado" },
  { value: 0, label: "D", name: "domingo" },
];
// Mismo tope que AI_MAX_REPLIES_CAP del backend.
const MAX_REPLIES_CAP = 50;
const ESCALATION_MAX = 300;

// Centinela del radio "Personalizado…": NUNCA viaja al backend (el payload
// siempre lleva model). Las listas curadas viven en aiModelCatalog.js (badge +
// pros/contras por modelo); el backend valida model como string libre, así que
// cualquier ID nuevo del proveedor entra por "Modelo personalizado…".
// o1-mini sigue FUERA de las listas a propósito: exige max_completion_tokens y
// rechaza mensajes system — con nuestro cliente fallaría en silencio.
const CUSTOM_MODEL = "__custom__";

// Perillas de personalización (capa 1) — keys = campos del backend.
const STYLE_DEFAULTS = {
  style_tone: "profesional",
  style_warmth: "normal",
  style_emoji: "pocos",
  style_length: "breve",
  style_address: "tu",
};

const STYLE_FIELDS = [
  { key: "style_tone", label: "Tono", desc: "Cómo suena el asistente al escribir.",
    options: [["profesional", "Profesional"], ["cercano", "Cercano"], ["formal", "Formal"]] },
  { key: "style_warmth", label: "Calidez", desc: "Cuánta cortesía y empatía agrega.",
    options: [["menos", "Menos"], ["normal", "Normal"], ["mas", "Más"]] },
  { key: "style_emoji", label: "Emojis", desc: "Si usa emojis en sus respuestas.",
    options: [["no", "No usar"], ["pocos", "Pocos"], ["si", "Sí"]] },
  { key: "style_length", label: "Extensión", desc: "Qué tan largas son las respuestas.",
    options: [["breve", "Breve"], ["normal", "Normal"], ["detallado", "Detallado"]] },
  { key: "style_address", label: "Tratamiento", desc: "Cómo se dirige al cliente.",
    options: [["tu", "De tú"], ["usted", "De usted"]] },
];

// Plantilla recomendada del system prompt (capa 2) — courier genérico.
// v2 (2026-07): redactada a partir de 80 conversaciones reales del WhatsApp
// del helpdesk. Hallazgos que la moldean: escriben tanto comercios que
// despachan como destinatarios; mensajes cortos e informales (mediana 24
// caracteres), números de pedido sueltos y varios códigos de golpe; los
// temas dominantes son estado/demora, retiros no realizados, entregas
// fallidas y cambios de dirección/teléfono; piden humano varias veces sin
// ser derivados — por eso la derivación va "a la primera". No duplica capa 0
// (seguridad, no inventar, formato) ni capa 1 (tono/estilo compilado).
const RECOMMENDED_PROMPT = `Eres el asistente virtual de [nombre de tu empresa], courier de última milla en Chile. Atiendes por WhatsApp a comercios que despachan con nosotros y a destinatarios que esperan un paquete; resuelve rápido o deja el caso listo para el equipo.

Ante consultas por un envío pide primero su número (o el teléfono del destinatario); si llegan varios códigos, ve de a uno.

Casos frecuentes:
- Estado o demora: entrega el estado y el último movimiento que conste.
- Entrega fallida: explica el motivo si consta y registra la nueva visita; el equipo la confirmará.
- Cambio de dirección o teléfono: anota el dato exacto y deriva; no lo apliques tú.
- Retiros en bodega del comercio: informa horario o estado; si el móvil no pasó, registra el reclamo y deriva.
- Devoluciones o cambios: explica el proceso general y deriva la coordinación.
- Cancelaciones: registra el pedido que vuelve a bodega y deriva la confirmación.
- Paquete dañado o perdido, o reclamo por la atención: reconoce la molestia, resume el caso y deriva.
- Horarios y cobertura: responde solo lo definido aquí: [horarios de reparto y retiro, zonas].

Si no sabes algo, el dato no consta o piden hablar con una persona (deriva a la primera): avisa que un agente seguirá por este chat y deja un resumen del caso.

Tono: chileno neutro y cercano, sin modismos forzados; mensajes cortos tipo WhatsApp (2-3 frases); una pregunta por mensaje.`;

// GOTCHA _modern-forms.scss: sus reglas de input/select/textarea le ganan a
// Tailwind, por eso las utilidades de tamaño/tipografía van con variante !.
const CONTROL_CLS = cn(
  // !py-0: _modern-forms.scss mete padding vertical propio que, dentro de los
  // 32px de md:!h-8, descentraba y recortaba el texto del control.
  // !text-base bajo md: Safari iOS hace zoom automático al enfocar cualquier
  // input/select/textarea con font-size < 16px y NO vuelve solo — el usuario
  // queda con la vista ampliada y desplazada. En ≥768px vuelven los 14px.
  "w-full h-11 md:!h-8 rounded-md border border-border bg-bg !px-3 !py-0 !text-base md:!text-sm text-fg",
  "focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent",
  "disabled:opacity-50 disabled:cursor-not-allowed",
);

const SECONDARY_BTN_CLS = cn(
  "inline-flex items-center justify-center gap-1.5 h-11 md:h-8 px-3 rounded-md text-xs font-medium",
  "border border-border bg-transparent text-fg hover:bg-bg-subtle",
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors",
  "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent",
);

// Chip del segmented off/byo. Función normal a nivel de módulo (NUNCA definir
// componentes dentro del render: remonta el subtree y mata el foco).
// Roving tabindex: solo el chip activo entra en el orden de tabulación; entre
// chips se navega con flechas (patrón radiogroup de WAI-ARIA).
function ModeChip({ active, onClick, label, describedBy, disabled = false }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      aria-describedby={describedBy}
      tabIndex={active ? 0 : -1}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "inline-flex items-center justify-center h-11 md:h-8 px-3 rounded-md text-xs font-medium",
        "transition-colors duration-150 ease-out motion-reduce:transition-none",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        active ? "hd-brand-solid bg-brand-500 text-white" : "text-fg-muted hover:text-fg hover:bg-bg-muted",
      )}
    >
      {label}
    </button>
  );
}

// ─── Base de conocimiento: compilar / parsear ───────────────────────────
// Compila instrucciones + artículos en un system_prompt plano (lo que recibe
// la IA). El KnowledgeBaseEditor también lo muestra en su CompiledPreview.
function compileKnowledgeBasePrompt(instructions, entries) {
  const parts = [];
  const header = (instructions || "").trim();
  if (header) parts.push(header);

  const valid = (entries || []).filter(
    (e) => (e.title || "").trim() && (e.content || "").trim(),
  );

  if (valid.length > 0) {
    parts.push("");
    parts.push("## Base de conocimiento");
    valid.forEach((entry) => {
      parts.push(`### ${entry.title.trim()}`);
      parts.push(entry.content.trim());
      parts.push("");
    });
  }

  return parts.join("\n").trim();
}

// Intenta extraer una base de conocimiento de un system_prompt legacy.
// Retorna { instructions, entries } o null si no hay marcadores.
function parseKnowledgeBaseFromPrompt(prompt) {
  const text = (prompt || "").trim();
  const startMarker = "---KNOWLEDGE_BASE---";
  const endMarker = "---END_KNOWLEDGE_BASE---";

  const startIdx = text.indexOf(startMarker);
  const endIdx = text.indexOf(endMarker);

  if (startIdx === -1 || endIdx === -1 || endIdx <= startIdx) return null;

  const instructions = text.slice(0, startIdx).trim();
  const jsonStr = text.slice(startIdx + startMarker.length, endIdx).trim();

  let entries = [];
  try {
    const parsed = JSON.parse(jsonStr);
    if (Array.isArray(parsed)) entries = parsed;
  } catch {
    // JSON roto: se pierde la KB pero se preservan las instrucciones.
  }

  return { instructions, entries };
}

// Estado de una cuenta cuando NO está operativa (el 'active' no se rotula:
// es lo normal). Se muestra porque apagar la IA en un número que ya está
// caído es información distinta a apagarla en uno que sí recibe.
const ACCOUNT_STATUS_LABELS = {
  pending: "sin conectar",
  inactive: "desconectado",
  error: "con error",
};

// Switch de alcance (canal / cuenta). role="switch" nativo del botón: un
// checkbox no comunica "encendido/apagado" en lectores de pantalla igual de
// bien, y acá no hay formulario que enviar. El área táctil se extiende con un
// ::before invisible: el riel mide 36×20 pero se toca cómodo en móvil.
function ScopeSwitch({ checked, onChange, disabled, label, describedBy }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      aria-describedby={describedBy}
      disabled={disabled}
      onClick={onChange}
      className={cn(
        "relative inline-flex items-center shrink-0 w-9 h-5 rounded-full",
        "before:absolute before:content-[''] before:-inset-3 md:before:-inset-1.5",
        "transition-colors duration-150 ease-out motion-reduce:transition-none",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-bg",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        checked ? "bg-brand-500" : "bg-bg-muted border border-border",
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "block w-3.5 h-3.5 rounded-full bg-white shadow-sm",
          "transition-transform duration-150 ease-out motion-reduce:transition-none",
          checked ? "translate-x-[18px]" : "translate-x-[3px]",
        )}
      />
    </button>
  );
}

// Título de sección: única escala permitida (12px uppercase).
function SectionTitle({ children }) {
  return (
    <h2 className="text-xs uppercase tracking-wide font-semibold text-fg-muted">{children}</h2>
  );
}

// Skeleton que espeja las secciones reales para que nada salte al cargar.
function AiSettingsSkeleton() {
  return (
    <div className="divide-y divide-border" aria-hidden="true">
      <div className="pb-6 space-y-3">
        <Skeleton className="h-4 w-24 rounded" />
        <Skeleton className="h-11 md:h-10 w-72 rounded-lg" />
        <Skeleton className="h-4 w-96 max-w-full rounded" />
      </div>
      <div className="py-6 space-y-3">
        <Skeleton className="h-4 w-44 rounded" />
        <Skeleton className="h-4 w-64 rounded" />
        <Skeleton className="h-4 w-64 rounded" />
        <Skeleton className="h-4 w-64 rounded" />
      </div>
      {/* Dónde responde */}
      <div className="py-6 space-y-3">
        <Skeleton className="h-4 w-36 rounded" />
        <Skeleton className="h-5 rounded" />
        <Skeleton className="h-5 rounded" />
      </div>
      <div className="py-6 space-y-3">
        <Skeleton className="h-4 w-32 rounded" />
        <Skeleton className="h-8 rounded-md" />
        <Skeleton className="h-8 rounded-md" />
        <Skeleton className="h-8 rounded-md" />
        <Skeleton className="h-8 rounded-md" />
        <Skeleton className="h-8 rounded-md" />
      </div>
      <div className="py-6 space-y-3">
        <Skeleton className="h-4 w-40 rounded" />
        <div className="grid sm:grid-cols-2 gap-4">
          <Skeleton className="h-11 md:h-8 rounded-md" />
          <Skeleton className="h-11 md:h-8 rounded-md" />
        </div>
      </div>
      <div className="py-6 space-y-3">
        <Skeleton className="h-4 w-28 rounded" />
        <Skeleton className="h-11 md:h-8 rounded-md" />
      </div>
      <div className="py-6 space-y-3">
        <Skeleton className="h-4 w-56 rounded" />
        <Skeleton className="h-40 rounded-md" />
      </div>
      <div className="pt-6">
        <Skeleton className="h-11 md:h-8 w-40 rounded-md" />
      </div>
    </div>
  );
}

// `caller` y `capabilities` son el rol de quien mira y las capacidades del
// catálogo de agentes (el padre ya los carga). Ambos son opcionales: sin ellos
// —backend viejo o montaje suelto— normalizeCaller() e isGrantable() dejan todo
// permitido y el formulario se comporta exactamente como antes.
export default function LegacyAssistantForm({ caller, capabilities, onSaved }) {
  const { t } = useTranslation();

  // Carga inicial
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [backendMissing, setBackendMissing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Formulario (espejo del doc del tenant)
  const [mode, setMode] = useState("off");
  const [provider, setProvider] = useState("anthropic");
  const [model, setModel] = useState("");
  const [systemPrompt, setSystemPrompt] = useState("");
  // Base de conocimiento estructurada (alternativa al textarea monolítico).
  const [knowledgeBase, setKnowledgeBase] = useState([]);
  const [kbInstructions, setKbInstructions] = useState("");
  const [kbMode, setKbMode] = useState("structured"); // "structured" | "free"
  // El token guardado JAMÁS llega completo: solo su presencia y los últimos 4.
  const [hasToken, setHasToken] = useState(false);
  const [tokenLast4, setTokenLast4] = useState("");
  // Lo que el usuario tipea; vacío = "no cambiar el token guardado".
  const [tokenInput, setTokenInput] = useState("");
  // Lo GUARDADO (GET o respuesta del PUT), no lo que se está editando: las
  // reglas del rol del backend comparan contra el documento del courier.
  const [savedMode, setSavedMode] = useState("off");
  const [savedOrdersAccess, setSavedOrdersAccess] = useState(true);
  // Último valor GUARDADO del canal del portal: con la config bloqueada es lo
  // único (además del apagado) que se puede guardar, y hace falta saber si
  // cambió para habilitar "Guardar".
  const [savedPortalChannel, setSavedPortalChannel] = useState(null);

  // Cuándo toma el control (espejo de los campos del backend).
  const [trigger, setTrigger] = useState("always"); // always|off_hours|first_reply
  const [businessDays, setBusinessDays] = useState([1, 2, 3, 4, 5]);
  const [businessStart, setBusinessStart] = useState("09:00");
  const [businessEnd, setBusinessEnd] = useState("18:00");
  const [maxReplies, setMaxReplies] = useState("0"); // string: es un input
  const [handoffOnAgentReply, setHandoffOnAgentReply] = useState(true);
  // Cortesía al derivar a humano (válvula siempre activa en byo).
  const [escalationMessage, setEscalationMessage] = useState("");
  // Select de modelo: true = "Personalizado…" con input libre visible.
  const [modelIsCustom, setModelIsCustom] = useState(false);
  // Perillas de personalización (capa 1).
  const [style, setStyle] = useState(STYLE_DEFAULTS);
  // Herramienta de datos: la IA consulta pedidos reales antes de responder.
  const [dataAccessOrders, setDataAccessOrders] = useState(true);
  // Canal "Portal de clientes": gatea si los clientes pueden ESCRIBIR por el
  // widget del portal, responda la IA o una persona — NO es alcance de la IA.
  // null = el GET no trajo el campo (backend viejo): switch deshabilitado y el
  // PUT no manda la clave.
  const [portalChannelEnabled, setPortalChannelEnabled] = useState(null);
  // Catálogo de proveedores/modelos: arranca con la copia local y GET catalog
  // lo reemplaza cuando el backend nuevo existe.
  const [catalog, setCatalog] = useState(AI_MODEL_CATALOG);

  // Alcance: dónde se aplica esta MISMA configuración.
  //  - `scope`: canales y cuentas tal como los devuelve el backend (fuente de
  //    las claves; el front nunca las inventa).
  //  - `channelStates` / `accountStates`: el estado que el usuario está
  //    editando. Al guardar, un número solo viaja como excepción cuando
  //    difiere de su canal — así la lista se poda sola.
  const [scope, setScope] = useState([]);
  const [scopeMissing, setScopeMissing] = useState(false);
  const [channelStates, setChannelStates] = useState({});
  const [accountStates, setAccountStates] = useState({});

  // Guardado / prueba de conexión
  const [saveState, setSaveState] = useState("idle"); // idle|saving|saved|error
  const [saveError, setSaveError] = useState("");
  const [testState, setTestState] = useState("idle"); // idle|testing|ok|error
  const [testError, setTestError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({}); // {provider?,model?,token?}

  const modelRef = useRef(null);
  const tokenRef = useRef(null);
  const savedTimerRef = useRef(null);

  // ── Permisos del rol ─────────────────────────────────────────────────────
  // Derivados antes de los callbacks que los leen (mismo orden que el editor
  // de agentes).
  const permissions = normalizeCaller(caller);
  const canGrantOrders = isGrantable(
    (Array.isArray(capabilities) ? capabilities : []).find(
      (c) => c?.id === ORDERS_CAPABILITY_ID,
    ),
  );
  // La config guardada lee pedidos y este rol no puede conceder ese acceso: el
  // backend solo le acepta apagar la IA (mismo criterio que su regla de
  // alcance). Todo lo demás queda de solo lectura.
  const ordersLocked = !canGrantOrders && savedOrdersAccess;
  // Proveedor, modelo y credencial son de admin (y con la config bloqueada
  // tampoco se tocan).
  const providerReadOnly = ordersLocked || !permissions.can_manage_provider;
  // Pasar a 'byo' es encender el motor: solo quien administra el proveedor. Si
  // ya estaba guardado así, volver a elegirlo no cambia nada.
  const canSelectByo = !providerReadOnly || savedMode === "byo";

  // Timers limpiados al desmontar (la confirmación "Guardado" se desvanece).
  useEffect(() => () => clearTimeout(savedTimerRef.current), []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadError(null);
    (async () => {
      // Catálogo de proveedores/modelos, ANTES de la config: el cálculo de
      // "¿el modelo guardado está en la lista?" usa estas listas. Try propio:
      // sin la ruta (404 de un backend viejo) o con fallo, vale la copia
      // local ya cargada en el estado — el catálogo jamás bloquea la vista.
      let cat = AI_MODEL_CATALOG;
      try {
        const catRes = await getAiModelCatalog();
        const normalized = normalizeAiCatalog(catRes);
        if (normalized) cat = normalized;
      } catch {
        // Copia local (incluye isEndpointMissing: backend sin GET catalog).
      }
      if (!alive) return;
      setCatalog(cat);

      try {
        const res = await getAiSettings();
        if (!alive) return;
        const s = res?.settings || {};
        setMode(s.mode === "byo" ? "byo" : "off");
        setSavedMode(s.mode === "byo" ? "byo" : "off");
        // Provider del GET solo si es uno que el catálogo conoce; cualquier
        // otro (o ausente) cae a anthropic, el default del backend.
        const loadedProvider = providerFromCatalog(cat, s.provider) ? s.provider : "anthropic";
        setProvider(loadedProvider);
        setModel(s.model || "");
        setSystemPrompt(s.system_prompt || "");
        // Base de conocimiento: campo dedicado > marcadores en system_prompt > legacy.
        if (Array.isArray(s.knowledge_base) && s.knowledge_base.length > 0) {
          setKnowledgeBase(s.knowledge_base);
          setKbInstructions(s.kb_instructions || "");
          setKbMode("structured");
        } else {
          const parsed = parseKnowledgeBaseFromPrompt(s.system_prompt || "");
          if (parsed) {
            setKnowledgeBase(parsed.entries);
            setKbInstructions(parsed.instructions);
            setKbMode("structured");
          } else if (s.system_prompt) {
            // Prompt legacy sin marcadores → modo libre para no perder el texto.
            setKbInstructions(s.system_prompt);
            setKbMode("free");
          }
          // Si no hay nada: se queda en structured con KB vacía (default).
        }
        setHasToken(!!s.has_token);
        setTokenLast4(s.token_last4 || "");
        setTrigger(["off_hours", "first_reply"].includes(s.trigger) ? s.trigger : "always");
        setBusinessDays(Array.isArray(s.business_days) ? s.business_days : [1, 2, 3, 4, 5]);
        setBusinessStart(s.business_start || "09:00");
        setBusinessEnd(s.business_end || "18:00");
        setMaxReplies(String(s.max_replies_per_case ?? 0));
        setHandoffOnAgentReply(s.handoff_on_agent_reply !== false);
        setEscalationMessage(s.escalation_message || "");
        const listValues = (providerFromCatalog(cat, loadedProvider)?.models || []).map(
          (m) => m.id,
        );
        // Modelo guardado fuera de la lista curada => "Personalizado…" con el
        // input visible y prellenado (el GET siempre trae un model no vacío).
        setModelIsCustom(!!s.model && !listValues.includes(s.model));
        setStyle({
          style_tone: s.style_tone || STYLE_DEFAULTS.style_tone,
          style_warmth: s.style_warmth || STYLE_DEFAULTS.style_warmth,
          style_emoji: s.style_emoji || STYLE_DEFAULTS.style_emoji,
          style_length: s.style_length || STYLE_DEFAULTS.style_length,
          style_address: s.style_address || STYLE_DEFAULTS.style_address,
        });
        setDataAccessOrders(s.data_access_orders !== false);
        setSavedOrdersAccess(s.data_access_orders !== false);
        // portal_channel_enabled solo lo traen backends nuevos. null = "no
        // vino": el switch se pinta deshabilitado y el PUT omite la clave (un
        // backend viejo la rechazaría o la ignoraría en silencio).
        const loadedPortal =
          typeof s.portal_channel_enabled === "boolean" ? s.portal_channel_enabled : null;
        setPortalChannelEnabled(loadedPortal);
        setSavedPortalChannel(loadedPortal);
        setBackendMissing(false);
      } catch (err) {
        if (!alive) return;
        if (isEndpointMissing(err)) setBackendMissing(true);
        else setLoadError(err?.message || t("No se pudo cargar la configuración."));
      }

      // El alcance viaja en su propia llamada y en su propio try: un backend
      // sin esta ruta (o un fallo suyo) NO debe impedir configurar la IA — la
      // sección simplemente no se muestra y el PUT no toca esos campos.
      try {
        const scopeRes = await getAiChannelScope();
        if (!alive) return;
        const channels = Array.isArray(scopeRes?.channels) ? scopeRes.channels : [];
        const states = initialScopeStates(channels);
        setScope(channels);
        setChannelStates(states.channelStates);
        setAccountStates(states.accountStates);
        setScopeMissing(false);
      } catch {
        // Sin alcance: el resto de la vista funciona igual (y el PUT omite
        // los campos, así que nada se pisa con una lista vacía).
        if (alive) setScopeMissing(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [reloadKey, t]);

  const handleRetry = useCallback(() => setReloadKey((k) => k + 1), []);

  const handleModeOff = useCallback(() => setMode("off"), []);
  const handleModeByo = useCallback(() => setMode("byo"), []);

  // Flechas dentro del radiogroup: con dos opciones, cualquier flecha salta a
  // la otra y el foco SIGUE a la selección (roving tabindex de WAI-ARIA). Sin
  // permiso para encender la IA propia, la flecha no la elige (el chip está
  // deshabilitado, pero el teclado no pasa por su onClick).
  const handleModeKeyDown = useCallback((e) => {
    if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key)) return;
    e.preventDefault();
    const group = e.currentTarget;
    setMode((prev) => (prev === "off" && canSelectByo ? "byo" : "off"));
    // El aria-checked se actualiza en el próximo render; recién ahí se enfoca.
    requestAnimationFrame(() => {
      group.querySelector('[aria-checked="true"]')?.focus();
    });
  }, [canSelectByo]);

  // ── KB mode toggle handlers ──────────────────────────────────────────────
  // Al pasar a estructurado: intenta extraer KB del prompt actual; si no hay
  // marcadores, el texto completo va a instrucciones.
  const handleKbModeStructured = useCallback(() => {
    if (kbMode === "structured") return;
    const parsed = parseKnowledgeBaseFromPrompt(systemPrompt);
    if (parsed) {
      setKbInstructions(parsed.instructions);
      setKnowledgeBase(parsed.entries);
    } else {
      setKbInstructions(systemPrompt);
    }
    setKbMode("structured");
  }, [kbMode, systemPrompt]);

  // Al pasar a libre: compila la KB actual y la vierte en el textarea.
  const handleKbModeFree = useCallback(() => {
    if (kbMode === "free") return;
    const compiled = compileKnowledgeBasePrompt(kbInstructions, knowledgeBase);
    if (compiled) setSystemPrompt(compiled);
    setKbMode("free");
  }, [kbMode, kbInstructions, knowledgeBase]);

  // Refs espejo de proveedor y catálogo para handlers con deps vacías
  // (referencia estable) sin capturar valores viejos.
  const providerRefValue = useRef(provider);
  providerRefValue.current = provider;
  const catalogRef = useRef(catalog);
  catalogRef.current = catalog;

  // Al cambiar de proveedor: modelo vacío o perteneciente a la LISTA curada
  // del proveedor anterior => precarga el recomendado del nuevo; un modelo
  // personalizado tipeado a mano se respeta (queda en Personalizado…).
  const handleProviderChange = useCallback((e) => {
    const next = e.target.value;
    const prevProvider = providerRefValue.current;
    setProvider(next);
    setFieldErrors((prev) => ({ ...prev, provider: undefined }));
    setModel((prev) => {
      const trimmed = (prev || "").trim();
      const cat = catalogRef.current;
      const prevList = (providerFromCatalog(cat, prevProvider)?.models || []).map((m) => m.id);
      if (!trimmed || prevList.includes(trimmed)) {
        setModelIsCustom(false);
        return recommendedModelId(providerFromCatalog(cat, next));
      }
      return prev;
    });
  }, []);

  const handleModelChange = useCallback((e) => {
    setModel(e.target.value);
    setFieldErrors((prev) => ({ ...prev, model: undefined }));
  }, []);

  // Radio de un modelo del catálogo: lo fija directo y esconde el input libre.
  const handleModelPick = useCallback((e) => {
    setModelIsCustom(false);
    setModel(e.target.value);
    setFieldErrors((prev) => ({ ...prev, model: undefined }));
  }, []);

  // Radio "Modelo personalizado…": revela el input prellenado con el modelo
  // actual (no lo borra: sirve de punto de partida para editar).
  const handleModelPickCustom = useCallback(() => {
    setModelIsCustom(true);
    setFieldErrors((prev) => ({ ...prev, model: undefined }));
  }, []);

  const handleUseRecommended = useCallback(() => {
    setModelIsCustom(false);
    setModel(
      (prev) =>
        recommendedModelId(providerFromCatalog(catalogRef.current, providerRefValue.current)) ||
        prev,
    );
    setFieldErrors((prev) => ({ ...prev, model: undefined }));
  }, []);

  const handleStyleChange = useCallback(
    (key) => (e) => {
      const value = e.target.value;
      setStyle((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  // Plantilla recomendada (capa 2): con texto propio pide confirmación;
  // vacío o ya-plantilla rellena directo.
  const handleUseTemplate = useCallback(() => {
    setSystemPrompt((prev) => {
      if (prev.trim() && prev !== RECOMMENDED_PROMPT) {
        const ok = window.confirm(
          t("Ya escribiste instrucciones. ¿Reemplazarlas por la plantilla recomendada?"),
        );
        if (!ok) return prev;
      }
      return RECOMMENDED_PROMPT;
    });
  }, [t]);

  const handleTokenChange = useCallback((e) => {
    setTokenInput(e.target.value);
    setFieldErrors((prev) => ({ ...prev, token: undefined }));
    // Un token nuevo invalida el resultado de la prueba anterior.
    setTestState("idle");
    setTestError("");
  }, []);

  const handlePromptChange = useCallback((e) => {
    setSystemPrompt(e.target.value.slice(0, PROMPT_MAX));
  }, []);

  const handleEscalationChange = useCallback((e) => {
    setEscalationMessage(e.target.value.slice(0, ESCALATION_MAX));
  }, []);

  const handleToggleDay = useCallback((day) => {
    setBusinessDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort(),
    );
  }, []);

  // Al apagar/encender un canal, las cuentas que iban EN LÍNEA con él lo
  // siguen; las que el usuario dejó distintas (excepciones) se conservan —
  // apagar WhatsApp no debe borrar el "pero este número sí atiende".
  // Función normal: se recrea por render pero no monta nada (no es un componente).
  const handleToggleChannel = (channel) => {
    const current = channelStates[channel] !== false;
    const next = !current;
    const accounts = scope.find((c) => c.channel === channel)?.accounts || [];
    setAccountStates((prev) => followChannel(accounts, prev, current, next));
    setChannelStates((prev) => ({ ...prev, [channel]: next }));
  };

  const handleToggleAccount = (key) => {
    setAccountStates((prev) => ({ ...prev, [key]: prev[key] === false }));
  };

  const byoDisabled = mode !== "byo";
  // Campos editables del formulario: además del modo, la config fuera del
  // alcance del rol los deja todos de solo lectura (solo se puede apagar).
  const editDisabled = byoDisabled || ordersLocked;
  // Proveedor, modelo y credencial: además, solo quien administra el motor.
  const providerDisabled = byoDisabled || providerReadOnly;
  const tokenTyped = tokenInput.trim();
  // Sin permiso sobre el motor igual se puede PROBAR la credencial guardada tal
  // cual (el backend lo acepta: no cambia proveedor, modelo ni token).
  const canTestSaved = !byoDisabled && providerReadOnly && hasToken && !tokenTyped;
  // Con la config bloqueada se puede QUITAR el acceso a pedidos (es un apagado
  // parcial que el backend acepta); concederlo no.
  const ordersToggleDisabled = ordersLocked ? false : editDisabled || !canGrantOrders;
  const canTest =
    (canTestSaved || (!providerDisabled && (hasToken || !!tokenTyped))) &&
    testState !== "testing";

  const handleTest = useCallback(async () => {
    setTestState("testing");
    setTestError("");
    try {
      // La prueba usa lo que el usuario VE en el formulario (proveedor y
      // modelo incluidos), no la config guardada: probar "OpenAI" recién
      // elegido contra el Anthropic guardado daba un resultado mentiroso.
      // api_token solo si hay algo tipeado; sin él el backend usa el guardado.
      const typed = tokenInput.trim();
      const payload = { provider, model: model.trim() || undefined };
      if (typed) payload.api_token = typed;
      const res = await testAiSettings(payload);
      if (res?.ok) {
        setTestState("ok");
      } else {
        setTestState("error");
        setTestError(res?.error || t("El proveedor rechazó la conexión."));
      }
    } catch (err) {
      setTestState("error");
      if (isEndpointMissing(err)) setBackendMissing(true);
      // Un 403 tipado (probar la credencial de un proveedor que este rol no
      // administra) se explica con su código; el resto cae al del servidor.
      else setTestError(aiErrorMessage(err, t) || t("No se pudo probar la conexión."));
    }
  }, [tokenInput, provider, model, t]);

  const handleSave = useCallback(async () => {
    // Validación solo estricta con byo: apagado se puede guardar siempre. Sin
    // permiso sobre el motor no se exige nada de él: sus campos están
    // deshabilitados y el guardado quedaría trabado por algo que este rol no
    // puede corregir (tampoco viajan en el payload).
    if (mode === "byo" && !providerReadOnly) {
      const errors = {};
      if (!provider) errors.provider = t("Elige un proveedor.");
      if (!model.trim()) errors.model = t("Indica el modelo a usar.");
      if (!hasToken && !tokenInput.trim()) errors.token = t("Ingresa el token de API de tu proveedor.");
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        // Foco en el primer control ofensor (modelo antes que token).
        if (errors.model && modelRef.current) modelRef.current.focus();
        else if (errors.token && tokenRef.current) tokenRef.current.focus();
        return;
      }
    }

    setSaveState("saving");
    setSaveError("");
    try {
      // El numérico viaja saneado: input vacío/NaN cae a 0 (sin límite).
      const maxParsed = Math.min(
        MAX_REPLIES_CAP,
        Math.max(0, parseInt(maxReplies, 10) || 0),
      );
      const payload = {
        mode,
        provider,
        model: model.trim(),
        // En modo estructurado el system_prompt se compila desde la KB; en modo
        // libre se usa el textarea directamente.
        system_prompt:
          kbMode === "structured"
            ? compileKnowledgeBasePrompt(kbInstructions, knowledgeBase)
            : systemPrompt,
        trigger,
        business_days: businessDays,
        business_start: businessStart || "09:00",
        business_end: businessEnd || "18:00",
        max_replies_per_case: maxParsed,
        handoff_on_agent_reply: handoffOnAgentReply,
        escalation_message: escalationMessage,
        data_access_orders: dataAccessOrders,
        ...style,
      };
      // Compatibilidad: portal_channel_enabled viaja SOLO si el GET lo trajo —
      // mandárselo a un backend viejo haría que lo rechazara o lo ignorara.
      if (portalChannelEnabled !== null) {
        payload.portal_channel_enabled = portalChannelEnabled;
      }
      // Alcance: solo si la sección cargó. Mandar {}/[] con la ruta caída
      // borraría la configuración de canales que el tenant ya tenía.
      if (!scopeMissing) {
        payload.channel_toggles = channelStates;
        // Una cuenta viaja SOLO cuando difiere de su canal: el resto hereda,
        // así una excepción no queda congelada si mañana cambia el canal.
        payload.account_overrides = buildAccountOverrides(
          scope,
          channelStates,
          accountStates,
        );
      }
      // El token va SOLO si el usuario tipeó uno nuevo: el PUT parcial no debe
      // pisar el guardado.
      const typed = tokenInput.trim();
      if (typed) payload.api_token = typed;

      // Base de conocimiento: viaja como campo extra. El backend puede ignorarlo;
      // el system_prompt compilado ya contiene todo lo que la IA necesita.
      payload.knowledge_base = knowledgeBase;
      payload.kb_instructions = kbMode === "structured" ? kbInstructions : "";

      // Sin permiso sobre el motor, proveedor, modelo y token NO viajan: el
      // backend los trata como cambio de admin si difieren de lo guardado, y el
      // GET completa un modelo por defecto que el documento puede no tener
      // (reenviarlo daría 403 sin que el usuario haya tocado nada).
      if (!permissions.can_manage_provider) {
        delete payload.provider;
        delete payload.model;
        delete payload.api_token;
      }

      // Config fuera del alcance del rol: el backend solo acepta el apagado y
      // el canal del portal (que no es un ajuste de la IA), y compara CADA clave
      // enviada contra lo guardado. Reenviar el formulario completo arriesgaría
      // un 403 por una diferencia de formato: viajan solo el modo y el portal.
      const body = ordersLocked
        ? {
            mode,
            // Quitarle a la IA el acceso a pedidos también es un apagado.
            ...(dataAccessOrders === false ? { data_access_orders: false } : {}),
            ...(portalChannelEnabled !== null
              ? { portal_channel_enabled: portalChannelEnabled }
              : {}),
          }
        : payload;

      const res = await saveAiSettings(body);
      const s = res?.settings || {};
      // La página que la contiene (interruptor maestro, bloqueo por pedidos)
      // depende de esta misma config: se le avisa lo guardado.
      onSaved?.(s);
      // Se refresca el enmascarado con la respuesta y se limpia el input: el
      // token nunca queda visible ni re-enviable por accidente.
      setHasToken(!!s.has_token);
      setTokenLast4(s.token_last4 || "");
      if (s.mode) {
        setMode(s.mode === "byo" ? "byo" : "off");
        setSavedMode(s.mode === "byo" ? "byo" : "off");
      }
      if (typeof s.data_access_orders === "boolean") {
        setSavedOrdersAccess(s.data_access_orders);
      }
      // Si el backend confirma el canal del portal, se toma su valor (y un
      // backend recién actualizado "enciende" el switch sin recargar).
      if (typeof s.portal_channel_enabled === "boolean") {
        setPortalChannelEnabled(s.portal_channel_enabled);
        setSavedPortalChannel(s.portal_channel_enabled);
      }
      setTokenInput("");
      setSaveState("saved");
      clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setSaveState("idle"), 2500);
    } catch (err) {
      if (isEndpointMissing(err)) {
        setBackendMissing(true);
        setSaveState("idle");
      } else {
        setSaveState("error");
        // Los 403 del contrato nuevo (rol sin permiso, ajuste por encima del
        // alcance del rol) traen su propia explicación; el resto cae al mensaje
        // del servidor o al genérico de la pantalla.
        setSaveError(aiErrorMessage(err, t) || t("No se pudo guardar la configuración."));
      }
    }
  }, [
    mode,
    provider,
    model,
    systemPrompt,
    tokenInput,
    hasToken,
    trigger,
    businessDays,
    businessStart,
    businessEnd,
    maxReplies,
    handoffOnAgentReply,
    escalationMessage,
    dataAccessOrders,
    portalChannelEnabled,
    style,
    scope,
    scopeMissing,
    channelStates,
    accountStates,
    kbMode,
    kbInstructions,
    knowledgeBase,
    permissions.can_manage_provider,
    providerReadOnly,
    ordersLocked,
    t,
  ]);

  // Dónde NO responde, en palabras: es la línea que evita el susto de "creí
  // que estaba atendiendo ese número". Un canal apagado se nombra una vez
  // (con sus excepciones aparte); si el canal está encendido, se nombran los
  // números apagados.
  const scopeOffLabels = scopeMissing
    ? []
    : disabledScopeLabels(scope, channelStates, accountStates);

  // Proveedor vigente según el catálogo (backend o copia local): de acá salen
  // la lista de modelos, el recomendado y el keyUrl del link de la API key.
  const currentProvider = providerFromCatalog(catalog, provider);
  const currentModels = currentProvider?.models || [];
  // ¿La opción marcada es "Personalizado…"? Misma condición para los radios,
  // el input libre y su texto de ayuda (calculada una vez).
  const modelSelectCustom = modelIsCustom || !currentModels.some((m) => m.id === model);

  const promptLen = systemPrompt.length;
  const escalationLen = escalationMessage.length;
  // Resumen "L M X J V" para el panel informativo (orden de pintado chileno).
  const daysSummary =
    DAY_PILLS.filter((d) => businessDays.includes(d.value))
      .map((d) => d.label)
      .join(" ") || t("ningún día");

  return (
    <div className="space-y-5">

    {loading && <AiSettingsSkeleton />}

    {!loading && backendMissing && (
      <PendingBackendNotice>
        {t("Esta función está en construcción.")}
      </PendingBackendNotice>
    )}

    {!loading && !backendMissing && loadError && (
      <Card padding="lg" className="max-w-md mx-auto text-center">
        <div className="flex items-center justify-center gap-1.5 text-xs text-status-error">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
          {loadError}
        </div>
        <button
          type="button"
          onClick={handleRetry}
          className="mt-2 h-8 px-3 rounded-md text-xs font-medium text-brand-700 hover:bg-brand-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors"
        >
          {t("Reintentar")}
        </button>
      </Card>
    )}

    {/* La config guardada está por encima del alcance del rol: se dice ARRIBA
        de todo, antes de que el usuario intente tocar un campo que no puede. */}
    {!loading && !backendMissing && !loadError && ordersLocked && (
      <div
        id="ai-settings-locked-help"
        role="note"
        className="flex items-start gap-2.5 rounded-lg border border-status-warning/30 bg-status-warning-soft/60 px-3 py-2.5"
      >
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-status-warning" aria-hidden="true" />
        <p className="text-[12px] md:text-[11px] leading-relaxed text-fg">
          {t("La IA de la empresa lee pedidos y tu rol no puede conceder ese acceso: solo puedes apagarla.")}
        </p>
      </div>
    )}

    {!loading && !backendMissing && !loadError && (
      <div className="divide-y divide-border">
        {/* ─── 1. Asistente ─────────────────────────────────────── */}
        <section className="pb-6 space-y-3">
          <SectionTitle>{t("Asistente")}</SectionTitle>
          <div
            role="radiogroup"
            aria-label={t("Estado del asistente")}
            onKeyDown={handleModeKeyDown}
            className="inline-flex items-center gap-1 rounded-lg border border-border bg-bg p-1"
          >
            <ModeChip
              active={mode === "off"}
              onClick={handleModeOff}
              label={t("Desactivado")}
            />
            <ModeChip
              active={mode === "byo"}
              onClick={handleModeByo}
              label={t("IA propia (BYO)")}
              describedBy={canSelectByo ? "ai-mode-help" : "ai-mode-help ai-mode-readonly"}
              disabled={!canSelectByo}
            />
          </div>
          <p id="ai-mode-help" className="text-xs text-fg-muted">
            {t(
              "Responde automáticamente los mensajes entrantes mientras la conversación no esté asignada a un agente.",
            )}
          </p>
          {!canSelectByo && (
            <p id="ai-mode-readonly" className="text-xs text-fg-muted">
              {/* El motivo real importa: con permiso sobre el proveedor, lo que
                  bloquea es el acceso a pedidos que este rol no puede conceder. */}
              {ordersLocked && permissions.can_manage_provider
                ? t("La IA de la empresa lee pedidos y tu rol no puede conceder ese acceso: solo puedes apagarla.")
                : t("Solo un administrador puede activar la IA propia.")}
            </p>
          )}

          {/* ¿Cuándo responde? — las reglas SIEMPRE activas + el
              resumen vivo de la configuración elegida. */}
          <div className={cn("border border-border rounded-md p-3 space-y-2", byoDisabled && "opacity-60")}>
            <p className="text-xs font-medium text-fg">{t("¿Cuándo responde el asistente?")}</p>
            <ul className="space-y-1 text-xs text-fg-muted list-disc pl-4">
              <li>{t("Solo en conversaciones sin agente asignado.")}</li>
              <li>{t("Nunca en grupos ni en casos resueltos.")}</li>
              <li>{t("Si el proveedor de IA falla, guarda silencio y la conversación sigue su flujo normal.")}</li>
              <li>{t("Freno anti-bucle: tras 10 respuestas de IA en 10 minutos deja de responder en esa conversación.")}</li>
              <li>{t("Si el contacto pide hablar con un humano, la IA se aparta de inmediato y la conversación queda pendiente para tu equipo.")}</li>
              {trigger === "always" && (
                <li>{t("Con tu configuración actual: responde todos los mensajes entrantes.")}</li>
              )}
              {trigger === "off_hours" && (
                <li>
                  {t(
                    "Con tu configuración actual: responde solo fuera del horario de atención ({{days}}, {{start}}–{{end}}, hora de Chile).",
                    { days: daysSummary, start: businessStart, end: businessEnd },
                  )}
                </li>
              )}
              {trigger === "first_reply" && (
                <li>{t("Con tu configuración actual: responde solo la primera vez en cada caso; después guarda silencio.")}</li>
              )}
              {Number(maxReplies) > 0 && (
                <li>{t("Máximo {{n}} respuestas de IA por caso.", { n: Number(maxReplies) })}</li>
              )}
              {scopeOffLabels.length > 0 && (
                <li>
                  {t("No responde en: {{lista}}.", {
                    lista: scopeOffLabels.join(", "),
                  })}
                </li>
              )}
              {handoffOnAgentReply && (
                <li>{t("Si un agente escribe en el caso, la IA deja de intervenir.")}</li>
              )}
            </ul>
          </div>
        </section>

        {/* ─── 1a. Dónde responde ──────────────────────────────────
            Misma configuración, encendida/apagada por canal y por
            número. Si el backend no expone la ruta, la sección
            simplemente no existe (y el PUT no toca esos campos). */}
        {!scopeMissing && scope.length > 0 && (
          <section className={cn("py-6 space-y-3", byoDisabled && "opacity-60")}>
            <SectionTitle>{t("Dónde responde")}</SectionTitle>
            <p id="ai-scope-help" className="text-xs text-fg-muted">
              {t(
                "La configuración es una sola; acá eliges dónde se aplica. Un número puede llevarle la contraria a su canal.",
              )}
            </p>

            <div className="divide-y divide-border">
              {scope.map((c) => {
                const channelOn = channelStates[c.channel] !== false;
                const accounts = c.accounts || [];
                return (
                  <div key={c.channel} className="py-3 first:pt-0 last:pb-0 space-y-3">
                    <div className="flex items-center justify-between gap-4">
                      <span className="min-w-0">
                        <span className="block text-xs font-medium text-fg truncate">
                          {c.label}
                        </span>
                        {accounts.length > 0 && (
                          <span className="block text-[12px] md:text-[11px] text-fg-muted">
                            {accounts.length === 1
                              ? t("1 cuenta conectada")
                              : t("{{n}} cuentas conectadas", { n: accounts.length })}
                          </span>
                        )}
                      </span>
                      <ScopeSwitch
                        checked={channelOn}
                        disabled={editDisabled}
                        onChange={() => handleToggleChannel(c.channel)}
                        label={t("Asistente en {{canal}}", { canal: c.label })}
                        describedBy="ai-scope-help"
                      />
                    </div>

                    {accounts.length > 0 && (
                      <ul className="space-y-2.5 pl-3 border-l border-border">
                        {accounts.map((a) => {
                          const on = accountStates[a.key] !== false;
                          // Difiere de su canal => es una excepción
                          // explícita, y así viaja en el guardado.
                          const isException = on !== channelOn;
                          return (
                            <li
                              key={a.key}
                              className="flex items-center justify-between gap-3"
                            >
                              <span className="min-w-0">
                                <span className="block text-xs text-fg truncate">
                                  {a.label}
                                </span>
                                <span className="flex items-center gap-1.5 min-w-0 text-[12px] md:text-[11px] text-fg-muted">
                                  <span className="truncate">
                                    {a.phone
                                      ? formatScopePhone(a.phone)
                                      : t("sin número asociado")}
                                  </span>
                                  {ACCOUNT_STATUS_LABELS[a.status] && (
                                    <span className="shrink-0">
                                      · {t(ACCOUNT_STATUS_LABELS[a.status])}
                                    </span>
                                  )}
                                  {isException && (
                                    <span className="shrink-0 inline-flex items-center rounded border border-border bg-bg-muted px-1.5 py-0.5 text-[11px] md:text-[10px] font-medium text-fg-muted">
                                      {t("excepción")}
                                    </span>
                                  )}
                                </span>
                              </span>
                              <ScopeSwitch
                                checked={on}
                                disabled={editDisabled}
                                onChange={() => handleToggleAccount(a.key)}
                                label={t("Asistente en {{cuenta}}", { cuenta: a.label })}
                              />
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ─── 1a-bis. Portal de clientes (canal de contacto) ──────
            NO es alcance de la IA: decide si los clientes pueden
            ESCRIBIR por el widget del portal, responda la IA o una
            persona. Por eso no se atenúa con byoDisabled ni vive
            dentro de "Dónde responde". Con backend viejo (el GET no
            trajo el campo) el switch queda deshabilitado con nota. */}
        <section className="py-6 space-y-3">
          <SectionTitle>{t("Canal de contacto")}</SectionTitle>
          <div className="flex items-center justify-between gap-4">
            <span className="min-w-0">
              <span className="block text-xs font-medium text-fg">
                {t("Portal de clientes")}
              </span>
              <span id="ai-portal-channel-help" className="block text-[12px] md:text-[11px] text-fg-muted">
                {t(
                  "Permite que tus clientes te contacten desde el portal. Si lo apagas, el chat del portal deja de estar disponible (independiente de si responde la IA o una persona).",
                )}
              </span>
            </span>
            <ScopeSwitch
              checked={portalChannelEnabled !== false}
              // El portal no es alcance de la IA: se puede cambiar aunque la
              // config de la IA esté fuera del alcance del rol.
              disabled={portalChannelEnabled === null}
              onChange={() => setPortalChannelEnabled((prev) => prev === false)}
              label={t("Portal de clientes")}
              describedBy="ai-portal-channel-help"
            />
          </div>
          {portalChannelEnabled === null && (
            <p className="text-[12px] md:text-[11px] text-fg-muted">
              {t("Esta opción está en construcción.")}
            </p>
          )}
        </section>

        {/* ─── 1b. Cuándo toma el control ─────────────────────────── */}
        <section className={cn("py-6 space-y-4", byoDisabled && "opacity-60")}>
          <SectionTitle>{t("Cuándo toma el control")}</SectionTitle>

          {/* Radios nativos verticales: accesibles sin roving manual. */}
          <div role="radiogroup" aria-label={t("Cuándo responde la IA")} className="space-y-2">
            {[
              {
                value: "always",
                label: t("Siempre"),
                desc: t("Responde todos los mensajes entrantes de conversaciones sin agente."),
              },
              {
                value: "off_hours",
                label: t("Solo fuera de horario"),
                desc: t(
                  "En horario de atención responde tu equipo; la IA cubre noches, fines de semana y feriados según los días que marques.",
                ),
              },
              {
                value: "first_reply",
                label: t("Solo la primera respuesta"),
                desc: t("La IA da una única respuesta de bienvenida por caso y luego cede la conversación."),
              },
            ].map((opt) => (
              <label
                key={opt.value}
                className={cn("flex items-start gap-2.5 cursor-pointer", editDisabled && "cursor-not-allowed")}
              >
                <input
                  type="radio"
                  name="ai-trigger"
                  value={opt.value}
                  checked={trigger === opt.value}
                  onChange={() => setTrigger(opt.value)}
                  disabled={editDisabled}
                  className="mt-0.5 !w-5 !h-5 md:!w-4 md:!h-4 accent-brand-500 shrink-0"
                />
                <span className="min-w-0">
                  <span className="block text-xs font-medium text-fg">{opt.label}</span>
                  <span className="block text-[12px] md:text-[11px] text-fg-muted">{opt.desc}</span>
                </span>
              </label>
            ))}
          </div>

          {/* Horario: SOLO visible con off_hours. */}
          {trigger === "off_hours" && (
            <div className="space-y-2 pl-6">
              <p className="block text-xs font-medium text-fg">
                {t("Horario de atención del equipo (America/Santiago)")}
              </p>
              <div role="group" aria-label={t("Días de atención")} className="flex flex-wrap gap-1.5">
                {DAY_PILLS.map((d) => {
                  const active = businessDays.includes(d.value);
                  return (
                    <button
                      key={d.value}
                      type="button"
                      aria-pressed={active}
                      aria-label={t(d.name)}
                      onClick={() => handleToggleDay(d.value)}
                      disabled={editDisabled}
                      className={cn(
                        "inline-flex items-center justify-center w-11 h-11 md:w-8 md:h-8 rounded-md text-xs font-medium border transition-colors",
                        "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
                        "disabled:opacity-50 disabled:cursor-not-allowed",
                        active
                          ? "hd-brand-solid bg-brand-500 border-transparent text-white"
                          : "border-border text-fg-muted hover:text-fg hover:bg-bg-muted",
                      )}
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-2">
                <label htmlFor="ai-biz-start" className="hd-field-label text-xs text-fg-muted">
                  {t("de")}
                </label>
                <input
                  id="ai-biz-start"
                  type="time"
                  value={businessStart}
                  onChange={(e) => setBusinessStart(e.target.value)}
                  disabled={editDisabled}
                  className={cn(CONTROL_CLS, "!w-auto")}
                />
                <label htmlFor="ai-biz-end" className="hd-field-label text-xs text-fg-muted">
                  {t("a")}
                </label>
                <input
                  id="ai-biz-end"
                  type="time"
                  value={businessEnd}
                  onChange={(e) => setBusinessEnd(e.target.value)}
                  disabled={editDisabled}
                  className={cn(CONTROL_CLS, "!w-auto")}
                />
              </div>
              <p className="text-[12px] md:text-[11px] text-fg-muted">
                {t("La IA responde FUERA de este horario. Dentro de él guarda silencio para que responda tu equipo.")}
              </p>
            </div>
          )}

          {/* Tope por caso */}
          <div className="space-y-1">
            <label htmlFor="ai-max-replies" className="hd-field-label block text-xs font-medium text-fg">
              {t("Máx. respuestas de IA por caso")}
            </label>
            <input
              id="ai-max-replies"
              type="number"
              inputMode="numeric"
              min={0}
              max={MAX_REPLIES_CAP}
              step={1}
              value={maxReplies}
              onChange={(e) => setMaxReplies(e.target.value)}
              disabled={editDisabled}
              className={cn(CONTROL_CLS, "sm:!w-32")}
            />
            <p className="text-[12px] md:text-[11px] text-fg-muted">{t("0 = sin límite")}</p>
          </div>

          {/* Handoff */}
          <label className={cn("flex items-start gap-2.5 cursor-pointer", editDisabled && "cursor-not-allowed")}>
            <input
              type="checkbox"
              checked={handoffOnAgentReply}
              onChange={(e) => setHandoffOnAgentReply(e.target.checked)}
              disabled={editDisabled}
              className="mt-0.5 !w-5 !h-5 md:!w-4 md:!h-4 accent-brand-500 shrink-0"
            />
            <span className="min-w-0">
              <span className="block text-xs font-medium text-fg">{t("Ceder al humano")}</span>
              <span className="block text-[12px] md:text-[11px] text-fg-muted">
                {t("Si un agente escribe en el caso, la IA deja de intervenir aunque la conversación siga sin asignar.")}
              </span>
            </span>
          </label>
        </section>

        {/* ─── 1c. Personalización (capa de estilo) ───────────── */}
        <section className={cn("py-6 space-y-4", byoDisabled && "opacity-60")}>
          <SectionTitle>{t("Personalización")}</SectionTitle>
          <p className="text-xs text-fg-muted">
            {t("Define cómo escribe el asistente. Se aplica además de tus instrucciones.")}
          </p>
          <div className="divide-y divide-border">
            {STYLE_FIELDS.map((f) => (
              <div
                key={f.key}
                // A6 · En móvil la columna de texto quedaba en 134px
                // contra un select de 144px fijo: descripciones en 2-3
                // líneas con el control centrado a media altura. Apilado
                // y a ancho completo cabe todo y mejora el tap target.
                className="flex flex-col items-stretch gap-1.5 md:flex-row md:items-center md:justify-between md:gap-4 py-2.5 first:pt-0 last:pb-0"
              >
                <div className="min-w-0">
                  <label htmlFor={`ai-${f.key}`} className="hd-field-label block text-xs font-medium text-fg">
                    {t(f.label)}
                  </label>
                  <p className="text-[12px] md:text-[11px] text-fg-muted">{t(f.desc)}</p>
                </div>
                <div className="relative shrink-0 w-full md:w-auto">
                  <select
                    id={`ai-${f.key}`}
                    value={style[f.key]}
                    onChange={handleStyleChange(f.key)}
                    disabled={editDisabled}
                    className={cn(CONTROL_CLS, "!w-full md:!w-36 appearance-none !pr-8")}
                  >
                    {f.options.map(([value, label]) => (
                      <option key={value} value={value}>
                        {t(label)}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-muted pointer-events-none"
                    aria-hidden="true"
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Herramienta de datos (Etapa B): la IA consulta pedidos
              reales del contacto antes de responder. Si el rol no puede
              conceder la consulta de pedidos, el interruptor queda
              deshabilitado (en cualquier sentido) con su motivo a la vista. */}
          <label className={cn("flex items-start gap-2.5 cursor-pointer pt-1", ordersToggleDisabled && "cursor-not-allowed")}>
            <input
              type="checkbox"
              checked={dataAccessOrders}
              onChange={(e) => setDataAccessOrders(e.target.checked)}
              // Con la config bloqueada se puede QUITAR el acceso a pedidos (es
              // un apagado parcial que el backend acepta); concederlo no.
              disabled={ordersToggleDisabled}
              aria-describedby={canGrantOrders ? undefined : "ai-orders-grant"}
              className="mt-0.5 !w-5 !h-5 md:!w-4 md:!h-4 accent-brand-500 shrink-0"
            />
            <span className="min-w-0">
              <span className="block text-xs font-medium text-fg">
                {t("Permitir a la IA consultar el estado de pedidos")}
              </span>
              <span className="block text-[12px] md:text-[11px] text-fg-muted">
                {t("Con esto activo, la IA busca los pedidos del contacto (por código o por su teléfono) antes de responder. Solo lee estado y comuna de destino; nunca direcciones exactas.")}
              </span>
              {!canGrantOrders && (
                <span id="ai-orders-grant" className="block text-[12px] md:text-[11px] text-fg-muted">
                  {t("Tu rol no permite conceder esta capacidad.")}
                </span>
              )}
            </span>
          </label>
        </section>

        {/* Con el asistente apagado, las secciones siguen visibles con
            el MISMO patrón, atenuadas y deshabilitadas (nada de
            "Próximamente" desalineado). */}
        <section
          className={cn("py-6 space-y-4", byoDisabled && "opacity-60")}
        >
          {/* ─── 2. Proveedor y modelo ──────────────────────────── */}
          <SectionTitle>{t("Proveedor y modelo")}</SectionTitle>
          {!permissions.can_manage_provider && (
            <p id="ai-provider-readonly" className="text-[12px] md:text-[11px] text-fg-muted">
              {t("Solo un administrador puede cambiar el proveedor y la credencial.")}
            </p>
          )}
          <div className="space-y-1 sm:max-w-xs">
            <label htmlFor="ai-provider" className="hd-field-label block text-xs font-medium text-fg">
              {t("Proveedor")}
            </label>
            <div className="relative">
              <select
                id="ai-provider"
                value={provider}
                onChange={handleProviderChange}
                disabled={providerDisabled}
                aria-describedby={permissions.can_manage_provider ? undefined : "ai-provider-readonly"}
                className={cn(CONTROL_CLS, "appearance-none !pr-8")}
              >
                {catalog.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
              <ChevronDown
                className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-muted pointer-events-none"
                aria-hidden="true"
              />
            </div>
            {fieldErrors.provider && (
              <p className="text-[12px] md:text-[11px] text-status-error" role="alert">
                {fieldErrors.provider}
              </p>
            )}
            {/* keyUrl del catálogo: decir dónde se crea el token ahorra
                el ticket de soporte más frecuente. */}
            {currentProvider?.keyUrl && (
              <a
                href={currentProvider.keyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  "inline-block text-[12px] md:text-[11px] font-medium text-brand-600 hover:underline",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded",
                  providerDisabled && "pointer-events-none opacity-50",
                )}
              >
                {t("¿Dónde consigo mi API key?")}
              </a>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <span className="block text-xs font-medium text-fg">{t("Modelo")}</span>
              <button
                type="button"
                onClick={handleUseRecommended}
                disabled={providerDisabled}
                className="text-[12px] md:text-[11px] font-medium text-brand-600 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
              >
                {t("usar recomendado")}
              </button>
            </div>

            {/* Radios nativos verticales (mismo patrón que "Cuándo toma
                el control"): cada modelo pinta label + badge del tier +
                una línea de pros y otra de contras del catálogo. */}
            <div
              role="radiogroup"
              aria-label={t("Modelo del asistente")}
              className="space-y-2.5"
            >
              {currentModels.map((m) => (
                <label
                  key={m.id}
                  className={cn(
                    "flex items-start gap-2.5 cursor-pointer",
                    providerDisabled && "cursor-not-allowed",
                  )}
                >
                  <input
                    type="radio"
                    name="ai-model-option"
                    value={m.id}
                    checked={!modelSelectCustom && model === m.id}
                    onChange={handleModelPick}
                    disabled={providerDisabled}
                    className="mt-0.5 !w-5 !h-5 md:!w-4 md:!h-4 accent-brand-500 shrink-0"
                  />
                  <span className="min-w-0">
                    <span className="flex items-center gap-1.5 flex-wrap min-w-0">
                      <span className="text-xs font-medium text-fg">{m.label}</span>
                      {AI_BADGE_LABELS[m.badge] && (
                        <span
                          className={cn(
                            "shrink-0 inline-flex items-center rounded border px-1.5 py-0.5 text-[12px] md:text-[10px] font-medium",
                            m.badge === "recomendado"
                              ? "border-brand-500 bg-brand-50 text-brand-700"
                              : "border-border bg-bg-muted text-fg-muted",
                          )}
                        >
                          {t(AI_BADGE_LABELS[m.badge])}
                        </span>
                      )}
                      <span className="text-[12px] md:text-[10px] text-fg-muted truncate">{m.id}</span>
                    </span>
                    {m.pros && (
                      <span className="block text-[12px] md:text-[11px] text-fg-muted">
                        <span className="font-medium text-fg">{t("Pros")}:</span> {m.pros}
                      </span>
                    )}
                    {m.cons && (
                      <span className="block text-[12px] md:text-[11px] text-fg-muted">
                        <span className="font-medium text-fg">{t("Contras")}:</span>{" "}
                        {m.cons}
                      </span>
                    )}
                  </span>
                </label>
              ))}

              {/* Personalizado…: entrada para IDs que el catálogo aún
                  no lista (el backend valida model como string libre). */}
              <label
                className={cn(
                  "flex items-start gap-2.5 cursor-pointer",
                  providerDisabled && "cursor-not-allowed",
                )}
              >
                <input
                  type="radio"
                  name="ai-model-option"
                  value={CUSTOM_MODEL}
                  checked={modelSelectCustom}
                  onChange={handleModelPickCustom}
                  disabled={providerDisabled}
                  className="mt-0.5 !w-5 !h-5 md:!w-4 md:!h-4 accent-brand-500 shrink-0"
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-medium text-fg">
                    {t("Modelo personalizado…")}
                  </span>
                  <span className="block text-[12px] md:text-[11px] text-fg-muted">
                    {t("Escribe el identificador exacto si tu proveedor lanzó un modelo que no está en la lista.")}
                  </span>
                  {modelSelectCustom && (
                    <>
                      <input
                        id="ai-model-custom"
                        ref={modelRef}
                        type="text"
                        value={model}
                        onChange={handleModelChange}
                        disabled={providerDisabled}
                        maxLength={AI_MODEL_MAX}
                        placeholder={recommendedModelId(currentProvider)}
                        aria-label={t("Identificador del modelo")}
                        aria-invalid={!!fieldErrors.model}
                        className={cn(CONTROL_CLS, "mt-1.5")}
                      />
                      <span className="block pt-1 text-[12px] md:text-[11px] text-fg-muted">
                        {t("Modelo fuera de la lista curada: usa Probar conexión para validarlo.")}
                      </span>
                    </>
                  )}
                </span>
              </label>
            </div>
            {fieldErrors.model && (
              <p className="text-[12px] md:text-[11px] text-status-error" role="alert">
                {fieldErrors.model}
              </p>
            )}
          </div>
        </section>

        {/* ─── 3. Credencial ──────────────────────────────────────── */}
        <section
          className={cn("py-6 space-y-4", byoDisabled && "opacity-60")}
        >
          <SectionTitle>{t("Credencial")}</SectionTitle>
          {!permissions.can_manage_provider && (
            <p id="ai-token-readonly" className="text-[12px] md:text-[11px] text-fg-muted">
              {t("Solo un administrador puede cambiar el proveedor y la credencial.")}
            </p>
          )}
          <div className="space-y-1">
            <label htmlFor="ai-token" className="hd-field-label block text-xs font-medium text-fg">
              {t("Token de API")}
            </label>
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <input
                  id="ai-token"
                  ref={tokenRef}
                  type="password"
                  autoComplete="new-password"
                  value={tokenInput}
                  onChange={handleTokenChange}
                  disabled={providerDisabled}
                  aria-describedby={permissions.can_manage_provider ? undefined : "ai-token-readonly"}
                  placeholder={hasToken ? `••••••••${tokenLast4}` : t("Pega aquí tu token")}
                  aria-invalid={!!fieldErrors.token}
                  className={CONTROL_CLS}
                />
              </div>
              <button
                type="button"
                onClick={handleTest}
                disabled={!canTest}
                className={cn(SECONDARY_BTN_CLS, "shrink-0 whitespace-nowrap")}
              >
                {testState === "testing" ? (
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                ) : null}
                {testState === "testing" ? t("Probando…") : t("Probar conexión")}
              </button>
            </div>
            {fieldErrors.token && (
              <p className="text-[12px] md:text-[11px] text-status-error" role="alert">
                {fieldErrors.token}
              </p>
            )}
            {/* Resultado de la prueba: inline, nunca toast. */}
            {testState === "ok" && (
              <p className="inline-flex items-center gap-1 rounded bg-status-success-soft text-status-success px-1.5 py-0.5 text-xs" role="status">
                <Check className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                {t("Conexión OK")}
              </p>
            )}
            {testState === "error" && (
              <p className="inline-flex items-center gap-1 rounded bg-status-error-soft text-status-error px-1.5 py-0.5 text-xs" role="alert">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                {testError}
              </p>
            )}
            {hasToken && (
              <p className="text-xs text-fg-muted">
                {t("Token guardado (termina en {{last4}}). Escribe uno nuevo para reemplazarlo.", {
                  last4: tokenLast4,
                })}
              </p>
            )}
            <p className="text-xs text-fg-muted">
              {t("El token se cifra en el servidor y nunca vuelve a mostrarse completo.")}
            </p>
          </div>
        </section>

        {/* ─── 4. Instrucciones ───────────────────────────────────── */}
        <section
          className={cn("py-6 space-y-4", byoDisabled && "opacity-60")}
        >
          <SectionTitle>{t("Instrucciones (system prompt)")}</SectionTitle>

          {/* Toggle estructurado / libre */}
          <div
            role="radiogroup"
            aria-label={t("Modo de edición del system prompt")}
            className="inline-flex items-center gap-1 rounded-lg border border-border bg-bg p-1"
          >
            <ModeChip
              active={kbMode === "structured"}
              onClick={handleKbModeStructured}
              label={t("Estructurado")}
              disabled={ordersLocked}
            />
            <ModeChip
              active={kbMode === "free"}
              onClick={handleKbModeFree}
              label={t("Modo libre")}
              disabled={ordersLocked}
            />
          </div>
          <p className="text-xs text-fg-muted">
            {kbMode === "structured"
              ? t("Organiza el conocimiento en tarjetas independientes. La IA recibe un system prompt compilado automáticamente.")
              : t("Escribe el system prompt completo manualmente. Ideal para prompts que no encajan en el formato de tarjetas.")}
          </p>

          {kbMode === "structured" ? (
            <KnowledgeBaseEditor
              entries={knowledgeBase}
              instructions={kbInstructions}
              onChange={setKnowledgeBase}
              onInstructionsChange={setKbInstructions}
              disabled={editDisabled}
            />
          ) : (
            <div className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <label htmlFor="ai-prompt" className="hd-field-label block text-xs font-medium text-fg">
                  {t("Cómo debe comportarse el asistente")}
                </label>
                <button
                  type="button"
                  onClick={handleUseTemplate}
                  disabled={editDisabled || systemPrompt === RECOMMENDED_PROMPT}
                  className="text-[12px] md:text-[11px] font-medium text-brand-600 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
                >
                  {t("Usar plantilla recomendada")}
                </button>
              </div>
              <textarea
                id="ai-prompt"
                value={systemPrompt}
                onChange={handlePromptChange}
                disabled={editDisabled}
                maxLength={PROMPT_MAX}
                rows={8}
                placeholder={t(
                  "Ej.: Eres el asistente de soporte de la empresa. Responde en español, breve y cordial…",
                )}
                className={cn(
                  "w-full min-h-[160px] rounded-md border border-border bg-bg !p-3 !text-base md:!text-sm text-fg resize-y",
                  "focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                )}
              />
              <div className="flex justify-end">
                <span
                  className={cn(
                    "text-[12px] md:text-[11px] tabular-nums",
                    promptLen >= PROMPT_MAX ? "text-status-error" : "text-fg-muted",
                  )}
                  aria-live="polite"
                >
                  {promptLen}/{PROMPT_MAX}
                </span>
              </div>
            </div>
          )}

          <div className="space-y-1">
            <label htmlFor="ai-escalation" className="hd-field-label block text-xs font-medium text-fg">
              {t("Mensaje al derivar a un humano")}
            </label>
            <textarea
              id="ai-escalation"
              value={escalationMessage}
              onChange={handleEscalationChange}
              disabled={editDisabled}
              maxLength={ESCALATION_MAX}
              rows={2}
              placeholder={t("Entendido, te comunico con una persona del equipo. En breve te atienden.")}
              className={cn(
                "w-full rounded-md border border-border bg-bg !p-3 !text-base md:!text-sm text-fg resize-y",
                "focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent",
                "disabled:opacity-50 disabled:cursor-not-allowed",
              )}
            />
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-fg-muted">
                {t("Es lo último que dice la IA antes de apartarse cuando el contacto pide un humano.")}
              </p>
              <span
                className={cn(
                  "text-[12px] md:text-[11px] tabular-nums shrink-0",
                  escalationLen >= ESCALATION_MAX ? "text-status-error" : "text-fg-muted",
                )}
                aria-live="polite"
              >
                {escalationLen}/{ESCALATION_MAX}
              </span>
            </div>
          </div>

          {/* Transparencia: la capa 0 existe aunque no sea editable acá. */}
          <p className="text-xs text-fg-muted">
            {t("Beartrack añade reglas base de seguridad y formato que no se pueden desactivar.")}
          </p>
        </section>

        {/* ─── 5. Guardar ─────────────────────────────────────────── */}
        <section className="pt-6">
          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={handleSave}
              // Con la config bloqueada lo único guardable es el apagado: sin
              // él (el modo sigue como estaba) no hay nada que mandar.
              disabled={
                saveState === "saving" ||
                (ordersLocked &&
                  mode === savedMode &&
                  dataAccessOrders !== false &&
                  portalChannelEnabled === savedPortalChannel)
              }
              aria-describedby={ordersLocked ? "ai-settings-locked-help" : undefined}
              className={cn(
                "inline-flex items-center justify-center gap-1.5 h-11 md:h-8 px-4 rounded-md text-xs font-medium",
                "hd-brand-solid bg-brand-500 text-white hover:bg-brand-600",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 transition-colors",
                "disabled:opacity-60 disabled:cursor-not-allowed",
              )}
            >
              {saveState === "saving" && (
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              )}
              {saveState === "saving" ? t("Guardando…") : t("Guardar cambios")}
            </button>
            {/* Confirmación que se desvanece: el span queda en el DOM y
                solo transiciona opacidad, sin saltos de layout. */}
            <span
              className={cn(
                "inline-flex items-center gap-1 text-xs text-fg-muted",
                "transition-opacity duration-500",
                saveState === "saved" ? "opacity-100" : "opacity-0",
              )}
              role="status"
              aria-hidden={saveState !== "saved"}
            >
              <Check className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
              {t("Guardado")}
            </span>
            {saveState === "error" && (
              <span className="inline-flex items-center gap-1 text-xs text-status-error" role="alert">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                {saveError}
              </span>
            )}
          </div>
        </section>
      </div>
    )}
    </div>
  );
}
